// Optimized Chatbot component with memoization and stable callbacks
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { IoMdSend } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { ChatState, initialState, updateChatState } from "./chatState";
import { paths } from "./paths";
import TypingIndicator from "./TypingIndicator";
import { getCurrentUser, syncUserWithServer } from "src/features/profile/state/profileSlice";
import { useAppDispatch } from "src/common/state/hooks";
import {
  resetActiveGptConversation,
  createChat,
  updateChat,
  askWixarpedia,
  setActiveGptConversation,
  fetchGptConversations,
  getScenarioHelperContextPdfs,
  resetScenarioHelperContextPdfs,
  addScenarioHelperContextPdf,
  setChosenLlmProvider,
  getChosenLlmProvider,
} from "../state/chatbotSlice";
import ChatBubble from "./ChatBubble";
import GraphBubble from "./GraphBubble";
import { IGptConversation } from "src/model/model";
import { useSelector, shallowEqual } from "react-redux";
import { GPT_CONVERSATION_LOADER_COMPONENT_ID } from "./GptConversationLoader";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import CreateProjectFromAiGraph from "./CreateProjectFromAiGraph";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import GeneratePdfFromScenarioHelperResponse from "./GeneratePdfFromScenarioHelperResponse";
import { generateHashFromString } from "../utils/generateHashFromString";
import { isMarkdownShapedLikeAScenario } from "../utils/isMarkdownShapedLikeAScenario";
import AttachContextPdfs from "./AttachContextPdfs";
import ContextCounter from "./ContextCounter";
import { IMedia } from "src/model/unityObject";
import { Tooltip } from "@mui/material";
import { mediaSelector } from "src/features/gallery/state/gallerySlice";
import LlmProviderDropdown from "./LlmProviderDropdown";
import "./Chatbot.scss";
import getUserPreferredLlmProvider from "../utils/getUserPrefferedLlmProvider";

interface Message {
  text: string;
  sender: "bot" | "user";
}

interface Option {
  value: string;
  label: string;
  next?: string;
}

interface ChatbotProps {
  mode: "scenarioHelper" | "wixarpedia";
  activeConversation?: IGptConversation | null;
  onGenerateProject?: () => void;
}

export const CHATBOT_COMPONENT_ID = "chatbotComponentId";
export const createProjectFromAiGraphModalId = "createProjectFromAiGraphModalId";

const Chatbot: React.FC<ChatbotProps> = ({ mode, activeConversation, onGenerateProject }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Manage free text interaction state
  const [hasInteracted, setHasInteracted] = useState(true);

  const loadInitialMessages = useCallback((): Message[] => {
    if (mode === "wixarpedia") {
      const saved = localStorage.getItem("wixarpediaChatMessages");
      return saved
        ? JSON.parse(saved)
        : [{ text: "Bonjour ! Comment puis-je vous aider ?", sender: "bot" }];
    }
    return [];
  }, [mode]);

  const [chatState, setChatState] = useState<ChatState>(initialState);
  const [currentPath, setCurrentPath] = useState("metier");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<Message[]>(loadInitialMessages);
  const [options, setOptions] = useState<Option[]>([]);
  const [userInput, setUserInput] = useState("");
  const [inputMode, setInputMode] = useState(true);
  const [sessionConversationId, setSessionConversationId] = useState<number | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);

  const user = useSelector(getCurrentUser, shallowEqual);
  const selectedPdfs = useSelector(getScenarioHelperContextPdfs, shallowEqual);
  const sourceMedias = useSelector(mediaSelector, shallowEqual);
  const chosenLlmProvider = useSelector(getChosenLlmProvider);

  const hasUnreadyPdfs = useMemo(
    () => selectedPdfs.some((pdf) => !pdf?.source_media_cache?.transcription),
    [selectedPdfs],
  );

  const isSubmitDisabled = useMemo(
    () => !userInput.trim() || isSubmitting || hasUnreadyPdfs,
    [userInput, isSubmitting, hasUnreadyPdfs],
  );

  const submitButtonStyles = useMemo(
    () => ({
      cursor: isSubmitDisabled ? "not-allowed" : "pointer",
      opacity: isSubmitDisabled ? 0.4 : 1,
    }),
    [isSubmitDisabled],
  );

  // Persist messages for wixarpedia
  useEffect(() => {
    if (mode === "wixarpedia") {
      localStorage.setItem("wixarpediaChatMessages", JSON.stringify(messages));
    }
  }, [messages, mode]);

  // Initialize conversation on mode or activeConversation change
  useEffect(() => {
    if (mode === "scenarioHelper") {
      activeConversation ? loadPreviousConversation() : resetChatLocally();
    } else {
      resetChatLocally();
    }
  }, [mode, activeConversation]);

  // Scroll chat into view
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  // Set active GPT conversation on user load
  useEffect(() => {
    if (user?.active_gpt_conversation?.id) {
      dispatch(setActiveGptConversation(user.active_gpt_conversation));
    }
  }, [user?.active_gpt_conversation?.id, dispatch]);

  // Set initial LLM provider
  useEffect(() => {
    const initial = getUserPreferredLlmProvider(user?.preferences);
    dispatch(setChosenLlmProvider(initial));
  }, [user?.preferences, dispatch]);

  // Ensure modal closed on mount
  useEffect(() => {
    dispatch(closeStandardModal(createProjectFromAiGraphModalId));
  }, [dispatch]);

  const loadPreviousConversation = useCallback(() => {
    if (!activeConversation) return;
    setHasInteracted(true);
    const { conversation_metadata, conversation_data } = activeConversation;
    setChatState((prev) => ({ ...prev, userFormResults: conversation_metadata.userFormResults }));
    const msgs: Message[] = [];
    const { userPrompts } = conversation_metadata;
    const { output } = conversation_data;
    const total = Math.max(userPrompts.length, output.length);
    for (let i = 0; i < total; i++) {
      if (i < userPrompts.length) msgs.push({ text: userPrompts[i], sender: "user" });
      if (i < output.length) msgs.push({ text: output[i], sender: "bot" });
    }
    setMessages(msgs);
    setInputMode(true);

    if (activeConversation.source_media_with_context?.length) {
      dispatch(resetScenarioHelperContextPdfs());
      activeConversation.source_media_with_context.forEach((pdf: any) => {
        return dispatch(addScenarioHelperContextPdf(pdf));
      });
    }
  }, [activeConversation, dispatch]);

  const resetChatLocally = useCallback(() => {
    setChatState(initialState);
    setCurrentPath("metier");
    const initial =
      mode === "scenarioHelper"
        ? "Bonjour. Quelle est votre problématique?"
        : "Bonjour ! Comment puis-je vous aider aujourd'hui ?";
    setMessages([{ text: initial, sender: "bot" }]);
    setIsBotTyping(false);
    setUserInput("");
    setSessionConversationId(null);
    if (mode === "wixarpedia") {
      localStorage.removeItem("wixarpediaChatMessages");
    }
    if (mode === "scenarioHelper") {
      setOptions(paths.metier.options);
      dispatch(resetActiveGptConversation());
      dispatch(resetScenarioHelperContextPdfs());
    }
  }, [mode, dispatch]);

  const updateBotMessage = useCallback((chunk: string) => {
    setMessages((prev) => {
      const msgs = [...prev];
      const i = msgs.length - 1;
      msgs[i] = { ...msgs[i], text: msgs[i].text + chunk };
      return msgs;
    });
  }, []);

  const handleOptionSelect = useCallback(
    (opt: Option) => {
      setMessages((prev) => [...prev, { text: opt.label, sender: "user" }]);
      setChatState((prev) => updateChatState(prev, currentPath as any, opt.value));
      setOptions([]);
      setIsBotTyping(true);
      setTimeout(() => {
        setIsBotTyping(false);
        if (opt.next) {
          setCurrentPath(opt.next);
          setMessages((prev) => [...prev, { text: paths[opt.next].question, sender: "bot" }]);
          setOptions(paths[opt.next]?.options || []);
        } else {
          setInputMode(true);
          setMessages((prev) => [
            ...prev,
            { text: "Merci. Veuillez décrire votre problématique.", sender: "bot" },
          ]);
        }
      }, 300);
    },
    [currentPath],
  );

  const handleUserPromptSubmit = useCallback(async () => {
    if (!userInput.trim()) return;
    const prompt = userInput;
    setUserInput("");
    setIsSubmitting(true);
    setMessages((prev) => [...prev, { text: prompt, sender: "user" }, { text: "", sender: "bot" }]);
    setIsBotTyping(true);
    setHasInteracted(true);
    if (mode === "scenarioHelper") {
      const conversationId = activeConversation?.id ?? sessionConversationId;
      if (conversationId) {
        await dispatch(
          updateChat({
            userPrompt: prompt,
            conversationId,
            cb: updateBotMessage,
          }),
        );
        dispatch(syncUserWithServer({}));
      } else {
        const result = await dispatch(
          createChat({
            userPrompt: prompt,
            userFormResults: chatState.userFormResults,
            sourceMediasWithContext: selectedPdfs.map((p) => Number(p.id)),
            chosenLlmProvider,
            sourceMedias,
            cb: updateBotMessage,
            cbOnRequestEnd: () => {
              dispatch(
                fetchGptConversations({
                  componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
                  start: 0,
                }),
              );
              dispatch(syncUserWithServer({}));
            },
          }),
        );
        const created = result.payload as IGptConversation | undefined;
        if (created?.id) {
          setSessionConversationId(created.id);
        }
      }
    } else {
      await dispatch(askWixarpedia({ userPrompt: prompt, cb: updateBotMessage }));
    }
    setIsSubmitting(false);
    setIsBotTyping(false);
  }, [
    activeConversation,
    sessionConversationId,
    chatState.userFormResults,
    createChat,
    askWixarpedia,
    updateChat,
    dispatch,
    userInput,
    mode,
    selectedPdfs,
    chosenLlmProvider,
    sourceMedias,
    updateBotMessage,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (userInput.trim() && !isSubmitting) handleUserPromptSubmit();
      }
    },
    [userInput, isSubmitting, handleUserPromptSubmit],
  );

  const handlePdfAdded = useCallback(
    (pdf: IMedia) => {
      if (!hasInteracted) return;
      setMessages((prev) => [
        ...prev,
        { text: `Fichier ajouté au contexte: ${pdf.filename}.`, sender: "bot" },
      ]);
    },
    [hasInteracted],
  );

  const renderedMessages = useMemo(
    () =>
      messages.map((message, idx) => (
        <div key={idx}>
          <ChatBubble customKey={idx} sender={message.sender} text={message.text} />
          {isMarkdownShapedLikeAScenario(message.text) && (
            <>
              <GraphBubble
                markdown={message.text}
                mdStringId={generateHashFromString(message.text)}
              />
              {!isSubmitting && (
                <div className="flex w-full justify-center">
                  {message.text && (
                    <GeneratePdfFromScenarioHelperResponse
                      markdown={message.text}
                      key={`${message.text}-${idx}`}
                    />
                  )}
                  <button
                    className="btn-primary-outline mt-1 w-3/5"
                    onClick={() =>
                      onGenerateProject
                        ? onGenerateProject()
                        : dispatch(openStandardModal(createProjectFromAiGraphModalId))
                    }
                  >
                    {t("pages.chat.generateModuleFromGraph")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )),
    [messages, isSubmitting, dispatch, t],
  );

  return (
    <div className="flex">
      <div className="flex m-2 w-full flex-col items-center">
        <div className="w-5/6 max-w-[80vw] h-[75vh] mx-auto p-4 bg-white shadow-lg rounded-lg flex flex-col">
          <div className="chat-window bg-gray-100 p-4 flex-grow overflow-auto rounded-md flex flex-col space-y-4">
            {renderedMessages}
            {isBotTyping && (
              <div className="flex justify-start">
                <div className="px-4 py-2 rounded-3xl bg-gray-300 text-black">
                  <TypingIndicator />
                </div>
              </div>
            )}
            {!isBotTyping && options.length > 0 && !hasInteracted && mode === "scenarioHelper" && (
              <div className="flex flex-col space-y-2">
                {options.map((opt) => (
                  <button
                    key={opt.value}
                    className="px-4 py-2 rounded-3xl bg-blue-500 text-white self-end"
                    onClick={() => handleOptionSelect(opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          {inputMode && (
            <div className="p-2 bg-white border-t">
              <div className="flex items-center">
                <div className="flex-grow px-4 py-2 rounded-3 bg-blue-100 text-black">
                  <textarea
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full bg-transparent outline-none resize-none overflow-y-auto max-h-40"
                    placeholder={
                      mode === "scenarioHelper"
                        ? t("pages.chat.scenarioHelperInputPlaceholder")
                        : t("pages.chat.wixarpediaInputPlaceholder")
                    }
                    disabled={isSubmitting}
                  />
                  {mode === "scenarioHelper" && (
                    <LlmProviderDropdown
                      defaultValue={chosenLlmProvider}
                      onChange={(value) => dispatch(setChosenLlmProvider(value))}
                      disabled={Boolean(activeConversation) || isSubmitting}
                    />
                  )}
                </div>
                <Tooltip
                  title={
                    hasUnreadyPdfs
                      ? t("pages.chat.pdfTranscodingTooltip")
                      : !userInput
                      ? t("pages.chat.noUserInputTooltip")
                      : ""
                  }
                >
                  <button
                    onClick={handleUserPromptSubmit}
                    disabled={isSubmitDisabled}
                    style={submitButtonStyles}
                    className="bg-blue-500 text-white w-12 h-12 rounded-md ml-2 flex items-center justify-center"
                  >
                    <IoMdSend size={24} />
                  </button>
                </Tooltip>
              </div>
              {mode === "scenarioHelper" && (
                <div className="flex justify-between items-center mt-2">
                  <AttachContextPdfs
                    onPdfAdded={handlePdfAdded}
                    isEditionEnabled={!activeConversation && !isSubmitting}
                  />
                  <ContextCounter promptText={userInput} chosenLlmProvider={chosenLlmProvider} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {!onGenerateProject && (
        <ModalStandard
          id={createProjectFromAiGraphModalId}
          isStatic
          modalType="standard"
          className="w-[60%] h-auto"
          showCloseButton
        >
          <CreateProjectFromAiGraph />
        </ModalStandard>
      )}
      <IsLoading componentId={CHATBOT_COMPONENT_ID} showSuccess={false} />
    </div>
  );
};

export default React.memo(Chatbot);
